import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <Link to="/" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900">Personal Insights</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: March 26, 2026</p>

        <div className="prose prose-gray max-w-none">
          <h2>Overview</h2>
          <p>
            Personal Insights is a personal knowledge workspace with an AI-curated news feed. Your
            personal items (links, notes, ideas) are stored in your own Google Drive. Feed features
            and the Developer API use a hosted database. We are committed to protecting your privacy
            and being transparent about how the application works.
          </p>

          <h2>Data Storage</h2>
          <p>
            <strong>Your personal items never touch our servers.</strong> All items you create
            (links, notes, ideas, podcasts, files, and tasks) are stored as JSON files in a folder
            called &ldquo;PersonalInsights&rdquo; in your personal Google Drive. Only you have
            access to this data through your Google account.
          </p>
          <p>
            Feed preferences, account profile information, and Developer API settings are stored in
            a hosted Supabase Postgres database (see sections below for details).
          </p>
          <p>The application stores the following files in your Google Drive:</p>
          <ul>
            <li>
              Item data files (links.json, podcasts.json, notes.json, ideas.json, files.json,
              tasks.json)
            </li>
            <li>Configuration file (config.json) containing your tags and app preferences</li>
            <li>Credentials file (credentials.json) if you configure an AI API key</li>
            <li>Uploaded files in an &ldquo;uploads&rdquo; subfolder</li>
            <li>
              Knowledge Base wiki pages in a &ldquo;wiki&rdquo; subfolder (AI-generated entity and
              concept pages that synthesize your saved items, only created when you use the
              Knowledge feature)
            </li>
          </ul>

          <h2>Authentication</h2>
          <p>
            Personal Insights uses Google OAuth 2.0 for authentication. When you sign in, we request
            the following permissions:
          </p>
          <ul>
            <li>
              <strong>drive.file</strong> &mdash; Access only to files created by this application
              in your Google Drive. We cannot see or access any other files in your Drive.
            </li>
            <li>
              <strong>userinfo.profile</strong> &mdash; Your name and profile picture, used to
              display your account information within the app.
            </li>
            <li>
              <strong>userinfo.email</strong> &mdash; Your email address, used to display your
              account information.
            </li>
          </ul>
          <p>
            Your Google access token is stored in your browser&apos;s local storage so the app can
            restore your session across tabs and browser restarts. The token is refreshed
            automatically while your Google session is still valid, and cleared when you sign out.
            We do not store your Google credentials on any server.
          </p>

          <h2>AI Features (Optional)</h2>
          <p>
            Personal Insights offers optional AI chat features powered by OpenRouter. These features
            are entirely opt-in &mdash; the app works fully without them.
          </p>
          <p>
            If you choose to use AI features, you provide your own OpenRouter API key. This key is
            stored in your browser&apos;s session storage and optionally persisted to your Google
            Drive (in credentials.json) for cross-session convenience. AI requests are sent directly
            from your browser to OpenRouter&apos;s API &mdash; they do not pass through our servers.
          </p>
          <p>
            When you use the AI chat, the content of the item you are chatting about is sent to
            OpenRouter as context. When you use the Knowledge Base &ldquo;Sync Knowledge&rdquo;
            feature, your saved item titles, descriptions, and content are sent to OpenRouter for
            entity and concept extraction. No data is sent to OpenRouter unless you explicitly
            initiate an AI chat or click Sync Knowledge.
          </p>

          <h2>Voice Input</h2>
          <p>
            Personal Insights offers an optional voice-to-text feature for creating notes. This
            feature uses your browser&apos;s built-in speech recognition (the Web Speech API), which
            is provided by Google in Chrome-based browsers.
          </p>
          <p>
            When you use voice input, audio is sent to Google&apos;s servers for real-time
            transcription. The audio is processed transiently and is not stored by Google. No audio
            data passes through our servers. Voice input is entirely opt-in &mdash; you must click
            the microphone button to activate it.
          </p>
          <p>
            Voice input is only available in browsers that support the Web Speech API (primarily
            Chrome and Edge). It is not available in Firefox or Safari.
          </p>

          <h2>Third-Party Services</h2>
          <p>The application interacts with the following third-party services:</p>
          <ul>
            <li>
              <strong>Google Drive API</strong> &mdash; For storing and syncing your personal data.
            </li>
            <li>
              <strong>Google OAuth</strong> &mdash; For authentication.
            </li>
            <li>
              <strong>Supabase</strong> &mdash; For the curated news feed (see below).
            </li>
            <li>
              <strong>Microlink API</strong> &mdash; For fetching link metadata (title, description,
              thumbnail) when you save a URL. Only the URL is sent; no personal data is transmitted.
            </li>
            <li>
              <strong>OpenRouter API</strong> (optional) &mdash; For AI chat features, using your
              own API key.
            </li>
            <li>
              <strong>Stripe</strong> (optional) &mdash; For processing Developer API subscription
              payments. Only used if you upgrade to a paid plan.
            </li>
          </ul>

          <h2>Feed Data (Supabase)</h2>
          <p>
            Feed features use Supabase for storing shared content items, classifications, and your
            feed preferences (topic tracking, source subscriptions, thumbs up/down). This data is
            stored in a hosted Supabase Postgres database. Your personal items (links, notes, ideas,
            etc.) remain in your Google Drive and are never sent to Supabase.
          </p>
          <p>
            Feed content is shared across all users and is not considered private. Your feed
            preferences (which topics you follow, which sources you subscribe to, and your
            thumbs-up/thumbs-down ratings) are associated with your user ID to personalize your
            experience.
          </p>

          <h2>Developer API</h2>
          <p>If you use the Developer API, the following additional data is stored in Supabase:</p>
          <ul>
            <li>
              <strong>API project settings</strong> &mdash; Your plan tier and usage limits,
              associated with your user ID.
            </li>
            <li>
              <strong>API keys</strong> &mdash; A cryptographic hash of each key you generate. The
              full key is shown once at creation and is not stored.
            </li>
            <li>
              <strong>Usage metrics</strong> &mdash; Daily request counts for rate limiting and
              billing purposes.
            </li>
          </ul>

          <h2>Payment Processing</h2>
          <p>
            Paid Developer API subscriptions are processed by{' '}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Stripe
            </a>
            . When you upgrade, you are redirected to Stripe&apos;s hosted checkout page. Your email
            address is shared with Stripe to pre-fill the checkout form. We store your Stripe
            customer ID and subscription ID to manage your plan, but we do not store your payment
            card details &mdash; those are held exclusively by Stripe.
          </p>

          <h2>Data Collection</h2>
          <p>
            We collect minimal data necessary to operate the service. For personal items, no data
            leaves your Google Drive. For feed features, we store your preferences and feed
            interactions in Supabase. For the Developer API, we store usage metrics and API key
            hashes. There is no analytics, tracking pixels, or telemetry. The application does not
            use cookies.
          </p>

          <h2>Cookies &amp; Local Storage</h2>
          <p>
            Personal Insights uses your browser&apos;s local storage to cache the Google access
            token and app folder ID, plus session storage for the in-memory OpenRouter API key
            cache. No cookies are set by the application.
          </p>

          <h2>Data Deletion</h2>
          <p>You have full control over your data:</p>
          <ul>
            <li>Delete individual items within the app</li>
            <li>Delete the entire &ldquo;PersonalInsights&rdquo; folder from your Google Drive</li>
            <li>Revoke API keys from the Settings page</li>
            <li>
              Cancel your Developer subscription via the Stripe billing portal or by contacting us
            </li>
            <li>
              Revoke the app&apos;s access from your{' '}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:underline"
              >
                Google Account permissions
              </a>
            </li>
          </ul>
          <p>
            To request deletion of your feed preferences, API project data, or Supabase account,
            contact us at the email below.
          </p>

          <h2>Children&apos;s Privacy</h2>
          <p>
            Personal Insights is not directed at children under the age of 13. We do not knowingly
            collect information from children.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. Changes will be reflected on this
            page with an updated revision date.
          </p>

          <h2>Contact</h2>
          <p>
            If you have questions about this privacy policy or need support, contact us at{' '}
            <a href="mailto:hello@insight-os.ai" className="text-brand-600 hover:underline">
              hello@insight-os.ai
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
